import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import ConfirmModal from '../components/common/ConfirmModal';
import { masterApi } from '../api';
import { useClinicEvent } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';

const EMPTY = { name: '', address: '', phone: '' };

/** Address book of the clinics and doctors who send patients here. */
export default function ReferralClinics() {
  const toast = useToast();

  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await masterApi.referrals();
      setClinics(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Someone adding a clinic at the other desk should show up here too.
  useClinicEvent(['masters:changed'], load);

  async function save(e) {
    e.preventDefault();
    if (!editing.name.trim()) {
      toast.warn('The clinic needs a name');
      return;
    }

    setSaving(true);
    try {
      if (editing._id) {
        await masterApi.updateReferral(editing._id, editing);
        toast.success(`${editing.name.trim()} updated`);
      } else {
        await masterApi.createReferral(editing);
        toast.success(`${editing.name.trim()} added to the list`);
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  const term = search.trim().toLowerCase();
  const visible = term
    ? clinics.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          (c.address || '').toLowerCase().includes(term) ||
          (c.phone || '').includes(term),
      )
    : clinics;

  return (
    <>
      <PageHeader
        title="Referral Clinics"
        subtitle="Clinics and doctors who send patients to us"
        icon="bi-hospital"
      >
        <Button className="btn-clinic" onClick={() => setEditing({ ...EMPTY })}>
          <i className="bi bi-plus-lg me-1" /> Add Clinic
        </Button>
      </PageHeader>

      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>
            <i className="bi bi-buildings me-2" />
            {clinics.length} clinic{clinics.length === 1 ? '' : 's'} on the list
          </span>
          <InputGroup style={{ maxWidth: 320 }}>
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, address or phone"
            />
          </InputGroup>
        </Card.Header>

        {loading ? (
          <Loader label="Loading referral clinics..." className="py-5" />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="bi-hospital"
            title={clinics.length === 0 ? 'No referral clinics yet' : 'Nothing matches that search'}
            hint={clinics.length === 0 ? 'Add the first one with the button above.' : undefined}
          />
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Clinic</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c._id}>
                    <td className="fw-semibold">{c.name}</td>
                    <td className="text-muted small">{c.address || '-'}</td>
                    <td className="small">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="text-decoration-none">
                          <i className="bi bi-telephone me-1" />
                          {c.phone}
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="btn-icon me-1"
                        onClick={() => setEditing({ ...c })}
                        title={`Edit ${c.name}`}
                      >
                        <i className="bi bi-pencil" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="btn-icon"
                        onClick={() => setDeleting(c)}
                        title={`Remove ${c.name}`}
                      >
                        <i className="bi bi-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        {editing && (
          <Form onSubmit={save}>
            <Modal.Header closeButton>
              <Modal.Title className="h5">
                <i className="bi bi-hospital me-2" style={{ color: 'var(--clinic-primary)' }} />
                {editing._id ? 'Edit' : 'Add'} Referral Clinic
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Label className="required">Clinic Name</Form.Label>
                  <Form.Control
                    required
                    autoFocus
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="e.g. Shree Krishna Clinic"
                  />
                </Col>

                <Col xs={12}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editing.address}
                    onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                    placeholder="Area, road, city"
                  />
                </Col>

                <Col xs={12}>
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    value={editing.phone}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value.replace(/[^\d+\- ]/g, '') })}
                    placeholder="10 digit number"
                    inputMode="numeric"
                    maxLength={15}
                  />
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="light" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" className="btn-clinic" disabled={saving}>
                <i className="bi bi-check-lg me-1" />
                {editing._id ? 'Save Changes' : 'Add Clinic'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      <ConfirmModal
        show={!!deleting}
        title="Remove this clinic?"
        body={deleting ? `"${deleting.name}" will come off the referral list. You can add it again later.` : null}
        confirmLabel="Remove"
        onHide={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await masterApi.deleteReferral(deleting._id);
            toast.success('Referral clinic removed');
            load();
          } catch (err) {
            toast.error(err);
          }
        }}
      />
    </>
  );
}
